import React, { FC, useCallback, useMemo } from 'react';

import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIIcon, { EIcon, EIconSize } from '@aquariuxfintech/aqx-trader-react-ui/icon';
import { showBasicSnackBar } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { useResetPasswordExperience } from '@aqx-core/common/services/reset-password-experience';
import { EUISnackBarMessageType } from '@aqx-core/common/services/snackbar';
import { useAppTheme } from '@aqx-core/common/theme';
import { openUrl } from '@aqx-member/web/utils/url/open-url';

import DemoCompletionDetailHeader from '../../../shared/demo-completion-step/detail-header';
import DemoCompletionDetailItem from '../../../shared/demo-completion-step/detail-item';
import { StyledDemoCompletionDetailsContainer } from '../../../shared/demo-completion-step/styled';

const ResetPasswordAccountDetails: FC = () => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const { details } = useResetPasswordExperience();

  const { mobileUrl, token, serverUrl, login, password } = details || {};

  const QRCodeValue = useMemo(() => {
    return `${mobileUrl}?token=${token}`;
  }, [mobileUrl, token]);

  const onCopy = () => {
    let textToCopy = '';

    if (serverUrl) {
      textToCopy += `ServerUrl: ${serverUrl}\n`;
    }

    if (login) {
      textToCopy += `Login: ${login}\n`;
    }
    if (password) {
      textToCopy += `Password: ${password}\n`;
    }

    navigator.clipboard.writeText(textToCopy).then(() =>
      showBasicSnackBar({
        messageType: EUISnackBarMessageType.SUCCESS,
        message: t('Copied to clipboard.'),
        delay: 1500
      })
    );
  };

  const onClickServerUrl = useCallback(() => {
    openUrl(serverUrl, '_blank');
  }, [serverUrl]);

  return (
    <StyledContainer>
      <DemoCompletionDetailHeader title={t('Demo account details')} onCopy={onCopy} />

      <StyledDemoCompletionDetailsContainer>
        <DemoCompletionDetailItem label={t('Server URL')} value={serverUrl} onClickLink={onClickServerUrl} />

        <DemoCompletionDetailItem label={t('Login')} value={login} />

        <DemoCompletionDetailItem label={t('Password')} value={password} />
      </StyledDemoCompletionDetailsContainer>

      <StyledExpiryContainer>
        <UIIcon icon={EIcon.INFO} size={EIconSize.S} color={theme.themeColors.icon.disabled} />
        <StyledExpiryText typography={ETypography.TEXT_M_REGULAR}>
          {t('This account will expire in 30 days.')}
        </StyledExpiryText>
      </StyledExpiryContainer>

      <StyledDivider />

      <StyledExperienceText typography={ETypography.TEXT_L_REGULAR_ITALIC}>
        {t('Experience our site on mobile')}
      </StyledExperienceText>
      <StyledQRContainer>
        <QRCode size={100} value={QRCodeValue} />
      </StyledQRContainer>
    </StyledContainer>
  );
};

export default ResetPasswordAccountDetails;

const StyledContainer = styled.div`
  margin-top: ${ESpacing.XXL}px;
`;

const StyledExpiryContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${ESpacing.M}px;
  gap: ${ESpacing.XXS}px;
`;

const StyledExpiryText = styled(UIText)`
  color: ${({ theme }) => theme.themeColors.text.subtle};
`;

const StyledDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.themeColors.background.active};
  margin: ${ESpacing.XL}px 0px;
`;

const StyledExperienceText = styled(UIText)`
  text-align: center;
  margin-bottom: ${ESpacing.XL}px;
`;

const StyledQRContainer = styled.div`
  display: flex;
  justify-content: center;
`;
